import React, { memo } from 'react';
import { CodeScannerInput, Select, Text, Input } from 'v3/components';
import UploadChecklistMedia from 'v3/components/UploadChecklistMedia';
import Tooltip from 'v3/components/Tooltip';
import api from 'services/api';
import { usePermission } from 'hooks';
import { styles } from '../styles';

import {
  isSelectableSlug,
  isDescriptiveSlug,
  isMediaSlug,
  hasBackAttachments,
  hasLocalFiles,
  isAnswered,
  isAttachmentRequiredByBackend,
  shouldRenderUpload,
  getQuestionSlug,
  isPlateSlug,
  safeStr,
} from '../Utils/helper';

function shouldShowSub(parentQuestion = {}, subQuestion = {}) {
  if (!parentQuestion?.id || parentQuestion.id !== subQuestion?.fatherQuestionId) return false;

  const questionTrigger = Array.isArray(parentQuestion?.questionTrigger)
    ? parentQuestion.questionTrigger
    : [];

  if (!questionTrigger.length) return false;

  const isSelectedTrigger = parentQuestion.responses.find(response => {
    return questionTrigger.find(
      trigger => response.id === trigger.id && response.selected
    );
  });

  return !!isSelectedTrigger;
}

const QuestionCard = memo(function QuestionCard({
  q,
  idx,
  saving,
  hasError = false,
  subQuestions = [],
  shouldShowSubQuestion = shouldShowSub,
  onToggleUnique,
  onToggleMultiple,
  onSetDescriptive,
  onSetPlate,
  onSetUpload,
  onRemoveFile,
  onOpenRegisterVehicle,
  onOpenRegisterWagon,
}) {
  const slug = getQuestionSlug(q);
  const isCodeReader = slug === 'code_reader' || slug === 'code';
  const isPlateQuestion = isPlateSlug(slug);
  const isVehiclePlate = slug === 'vehicle_plate';
  const hasPermissionToRegisterVehicle = usePermission('CADASTRAR_VEICULOS');
  const hasPermissionToRegisterWagon = usePermission('CADASTRAR_IMPLEMENTO');
  const questionVehicle = q?.vehicle || null;
  const questionWagon = q?.wagon || null;

  const attachmentRequired = isAttachmentRequiredByBackend(q);
  const showUpload = shouldRenderUpload(q);

  const okCard =
    isAnswered(q) &&
    (!attachmentRequired || hasBackAttachments(q) || hasLocalFiles(q));

  const normalizePlateOptions = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.rows)) return data.rows;
    if (data && typeof data === 'object') return [data];
    return [];
  };

  const selectedPlateOption =
    q?.plateOption ||
    (q?.questionDescriptiveResponse
      ? {
        id: q.questionDescriptiveResponse,
        plate: q.questionDescriptiveResponse,
      }
      : null);

  const fetchVehiclePlate = async (search) => {
    try {
      const response = await api.get('vehicle/license_plate', {
        params: { search },
      });
      return normalizePlateOptions(response?.data);
    } catch (error) {
      return [];
    }
  };

  const fetchImplementPlate = async (search) => {
    try {
      const response = await api.get('plate/wagons', {
        params: { search },
      });
      return normalizePlateOptions(response?.data);
    } catch (error) {
      return [];
    }
  };

  return (
    <div style={styles.questionCard({ active: okCard, error: hasError, })} >
      <div style={styles.qTop}>
        <div style={styles.qNumber}>{typeof idx === 'string' ? idx : idx + 1}</div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={styles.qText}>
            {q.question}
            {q.required ? <span style={{ color: '#DC2626' }}> *</span> : null}
          </div>
          <div style={styles.qDivider} />
        </div>
      </div>

      {(slug === 'unique' || slug === 'logic') && (
        <div style={styles.boolRow}>
          {(q.responses || []).map((r) => {
            const label = String(r?.description || '');
            const isLong = label.length >= 26;

            return (
              <button
                key={r.id}
                type="button"
                style={{
                  ...styles.boolBtn(!!r.selected),
                  ...(isLong ? { flexBasis: '100%' } : null),
                }}
                onClick={() => onToggleUnique(q.id, r.id)}
                disabled={saving}
                title={label}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {slug === 'multiple' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(q.responses || []).map((r) => (
            <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="checkbox"
                checked={!!r.selected}
                onChange={() => onToggleMultiple(q.id, r.id)}
                disabled={saving}
              />
              <Text>{r.description}</Text>
            </label>
          ))}
        </div>
      )}

      {isPlateQuestion && (
        questionVehicle || questionWagon ? (
          <Input
            label={isVehiclePlate ? 'Placa do veículo de tração' : 'Placa do implemento / Carroceria'}
            disabled
            value={
              isVehiclePlate
                ? safeStr(questionVehicle?.plate).toUpperCase()
                : safeStr(questionWagon?.plate).toUpperCase()
            }
          />
        ) : (
          <div>
            <Select.Async
              label={isVehiclePlate ? 'Placa do veículo de tração' : 'Placa do implemento / Carroceria'}
              onSearch={isVehiclePlate ? fetchVehiclePlate : fetchImplementPlate}
              value={selectedPlateOption}
              onChange={(value) => onSetPlate(q.id, value)}
              removeInputMin
              getOptionLabel={(option) => {
                let label = safeStr(option?.plate).toUpperCase();
                if (option?.vehicle?.plate) {
                  label += ` - Tração ${safeStr(option.vehicle.plate).toUpperCase()}`;
                }
                return label;
              }}
              getOptionValue={(option) => option?.id}
              isDisabled={saving}
            />
            {isVehiclePlate && hasPermissionToRegisterVehicle && (
              <div style={{ marginTop: 6 }}>
                <Tooltip content={<Text type="label">Cadastro simplificado de veículo</Text>}>
                  <Text
                    type="label"
                    weight="500"
                    color="dark_blue"
                    clickable
                    onClick={() => onOpenRegisterVehicle?.(q.id)}
                    id="button-checklist-veiculo-cadastrar"
                  >
                    Veículo novo? Clique aqui para cadastrar
                  </Text>
                </Tooltip>
              </div>
            )}
            {!isVehiclePlate && hasPermissionToRegisterWagon && (
              <div style={{ marginTop: 6 }}>
                <Tooltip content={<Text type="label">Cadastro simplificado de implemento</Text>}>
                  <Text
                    type="label"
                    weight="500"
                    color="dark_blue"
                    clickable
                    onClick={() => onOpenRegisterWagon?.(q.id)}
                    id="button-checklist-implemento-cadastrar"
                  >
                    Implemento novo? Clique aqui para cadastrar
                  </Text>
                </Tooltip>
              </div>
            )}
          </div>
        )
      )}

      {isCodeReader && (
        <CodeScannerInput
          label="Código"
          value={q.questionDescriptiveResponse || ''}
          onChange={(value) => onSetDescriptive(q.id, value)}
          disabled={saving}
        />
      )}

      {!isCodeReader && !isPlateQuestion && isDescriptiveSlug(slug) && (
        <textarea
          value={q.questionDescriptiveResponse || ''}
          onChange={(e) => onSetDescriptive(q.id, e.target.value)}
          placeholder="Digite sua resposta..."
          style={styles.textarea}
          disabled={saving}
        />
      )}

      {!isCodeReader &&
        !isPlateQuestion &&
        !isSelectableSlug(slug) &&
        !isDescriptiveSlug(slug) &&
        !isMediaSlug(slug) && (
          <textarea
            value={q.questionDescriptiveResponse || ''}
            onChange={(e) => onSetDescriptive(q.id, e.target.value)}
            placeholder="Digite sua resposta..."
            style={styles.textarea}
            disabled={saving}
          />
        )}

      {showUpload && (
        <div style={{ marginTop: 12 }}>
          <UploadChecklistMedia
            name="file"
            label="Inserir arquivo"
            accept=".jpg,.jpeg,.png,.pdf,.mkv,.mp4,.mov"
            multiple
            disabled={saving}
            files={Array.from(q?.attachment?.files || [])}
            onFilesChange={(nextFiles) => onSetUpload(q.id, nextFiles)}
            onRemove={(index) => onRemoveFile(q.id, index)}
          />
        </div>
      )}

      {subQuestions.length > 0 && (
        <div style={{ marginTop: 16, paddingLeft: 16, borderLeft: '3px solid #E5E7EB' }}>
          {subQuestions.map((subQ, subIdx) => {
            if (!shouldShowSubQuestion(q, subQ)) return null;

            const subSlug = getQuestionSlug(subQ);
            const subIsCodeReader = subSlug === 'code_reader' || subSlug === 'code';
            const subShowUpload = shouldRenderUpload(subQ);
            const baseIndex = typeof idx === 'string' ? idx : idx + 1;
            const subLabel = `${baseIndex}.${subIdx + 1}`;

            return (
              <div key={subQ.id} style={{ marginTop: subIdx > 0 ? 12 : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div
                    style={{
                      ...styles.qNumber,
                      width: 22,
                      height: 22,
                      fontSize: 11,
                      background: '#F3F4F6',
                    }}
                  >
                    {subLabel}
                  </div>
                  <div style={{ ...styles.qText, fontSize: 13, color: '#6B7280' }}>
                    {subQ.question}
                    {subQ.required ? <span style={{ color: '#DC2626' }}> *</span> : null}
                  </div>
                </div>

                {subIsCodeReader && (
                  <CodeScannerInput
                    label="Código *"
                    value={subQ.questionDescriptiveResponse || ''}
                    onChange={(value) => onSetDescriptive(subQ.id, value)}
                    disabled={saving}
                  />
                )}

                {!subIsCodeReader && isDescriptiveSlug(subSlug) && (
                  <textarea
                    value={subQ.questionDescriptiveResponse || ''}
                    onChange={(e) => onSetDescriptive(subQ.id, e.target.value)}
                    placeholder="Digite sua resposta..."
                    style={{ ...styles.textarea, fontSize: 13 }}
                    disabled={saving}
                  />
                )}

                {subShowUpload && (
                  <div style={{ marginTop: 8 }}>
                    <UploadChecklistMedia
                      name="file"
                      label="Inserir arquivo"
                      accept=".jpg,.jpeg,.png,.pdf,.mkv,.mp4,.mov"
                      multiple
                      disabled={saving}
                      files={Array.from(subQ?.attachment?.files || [])}
                      onFilesChange={(nextFiles) => onSetUpload(subQ.id, nextFiles)}
                      onRemove={(index) => onRemoveFile(subQ.id, index)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

export default QuestionCard;
